import { useSignOut } from "@features/auth/use-auth";
import { useProfileStore } from "@features/profile/profile.store";
import { cn } from "@shared/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@shared/ui/avatar";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@shared/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@shared/ui/sidebar";
import { Link } from "@tanstack/react-router";
import { EllipsisVertical, LogOut, Settings, Shield, User } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";

export function NavUser() {
	const { isMobile } = useSidebar();
	const profile = useProfileStore((state) => state.profile);
	const signOut = useSignOut();
	const intl = useIntl();

	const onSignOut = () => {
		signOut.mutate();
	};

	const navItems = [
		{
			key: "/profile/general",
			label: intl.formatMessage({
				id: "page.profile.general.title",
				defaultMessage: "General",
			}),
			icon: <User className="size-4" />,
		},
		{
			key: "/profile/security",
			label: intl.formatMessage({
				id: "page.profile.security.title",
				defaultMessage: "Security",
			}),
			icon: <Shield className="size-4" />,
		},
		{
			key: "/profile/settings",
			label: intl.formatMessage({
				id: "page.profile.settings.title",
				defaultMessage: "Settings",
			}),
			icon: <Settings className="size-4" />,
		},
	];
	const initials = `${profile?.first_name[0] ?? ""}${profile?.last_name[0] ?? ""}`.toUpperCase();

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<SidebarMenuButton
							size="lg"
							className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
						>
							<Avatar className="h-8 w-8 rounded-lg">
								<AvatarImage src={profile?.avatar_url ?? undefined} alt={initials} />
								<AvatarFallback className="rounded-lg bg-primary text-primary-foreground text-xs">
									{profile?.email?.slice(0, 2).toUpperCase()}
								</AvatarFallback>
							</Avatar>
							<div className="grid flex-1 text-left text-sm leading-tight">
								<span className="truncate font-medium">{profile?.first_name}</span>
								<span className="truncate text-xs text-muted-foreground">
									{profile?.email?.split("@")[0]}
								</span>
							</div>
							<EllipsisVertical className="ml-auto size-4" />
						</SidebarMenuButton>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
						side={isMobile ? "bottom" : "right"}
						align="end"
						sideOffset={4}
					>
						<DropdownMenuLabel className="p-0 font-normal">
							<div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
								<Avatar className="h-8 w-8 rounded-lg">
									<AvatarImage src={profile?.avatar_url ?? undefined} alt={initials} />
									<AvatarFallback className="rounded-lg">
										{profile?.email?.slice(0, 2).toUpperCase()}
									</AvatarFallback>
								</Avatar>
								<div className="grid flex-1 text-left text-sm leading-tight">
									<span className="truncate font-medium">{profile?.first_name}</span>
									<span className="truncate text-xs text-muted-foreground">
										{profile?.email?.split("@")[0]}
									</span>
								</div>
							</div>
						</DropdownMenuLabel>
						<DropdownMenuSeparator />
						<DropdownMenuGroup>
							{navItems.map((item) => (
								<DropdownMenuItem className="cursor-pointer" key={item.key} asChild>
									<Link to={item.key}>
										{item.icon}
										<span className="ms-1">{item.label}</span>
									</Link>
								</DropdownMenuItem>
							))}
						</DropdownMenuGroup>
						<DropdownMenuSeparator />
						<DropdownMenuItem
							onClick={onSignOut}
							disabled={signOut.isPending}
							className="text-destructive cursor-pointer"
						>
							<LogOut
								className={cn("me-1 size-4", {
									"animate-spin": signOut.isPending,
								})}
							/>
							<span>
								<FormattedMessage id="page.profile.signOut" defaultMessage="Sign out" />
							</span>
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</SidebarMenuItem>
		</SidebarMenu>
	);
}
